import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { wsUrl } from "@/lib/chat";
import { getAuthToken } from "@/lib/authStorage";

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [openConv, setOpenConv] = useState(null);
  const [lastMessage, setLastMessage] = useState(null);
  const [typingEvent, setTypingEvent] = useState(null);
  const [wsLive, setWsLive] = useState(false);
  const wsRef = useRef(null);

  const fetchConvs = useCallback(async () => {
    try {
      const r = await api.get("/conversations");
      setConvs(r.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!user) {
      setConvs([]);
      setInboxOpen(false);
      setOpenConv(null);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      setWsLive(false);
      return;
    }
    fetchConvs();
    const token = getAuthToken();
    if (!token) return;
    let stop = false;
    let ws;
    const connect = () => {
      ws = new WebSocket(wsUrl(token));
      wsRef.current = ws;
      ws.onopen = () => setWsLive(true);
      ws.onerror = () => setWsLive(false);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message" || data.type === "offer_update") {
            setLastMessage(data);
            fetchConvs();
          } else if (data.type === "typing") {
            setTypingEvent({ ...data, at: Date.now() });
          }
        } catch (_) {}
      };
      ws.onclose = () => {
        setWsLive(false);
        if (!stop) setTimeout(connect, 3000);
      };
    };
    connect();
    return () => { stop = true; if (ws) ws.close(); };
  }, [user, fetchConvs]);

  const openChatForCar = async (car) => {
    const r = await api.post("/conversations", { car_id: car.id });
    setInboxOpen(true);
    setOpenConv(r.data);
    fetchConvs();
    return r.data;
  };
  const openInbox = () => {
    setInboxOpen(true);
    fetchConvs();
  };
  const openChatConv = (conv) => {
    setInboxOpen(true);
    setOpenConv(conv);
  };
  const backToInbox = () => {
    setOpenConv(null);
    fetchConvs();
  };
  const closePanel = () => {
    setInboxOpen(false);
    setOpenConv(null);
    fetchConvs();
  };
  const closeChat = () => closePanel();
  const sendTyping = (conversationId) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: "typing", conversation_id: conversationId }));
    }
  };
  const totalUnread = (convs || []).reduce((s, c) => s + (c.unread || 0), 0);

  return (
    <ChatContext.Provider
      value={{
        convs,
        inboxOpen,
        openConv,
        lastMessage,
        typingEvent,
        wsLive,
        openChatForCar,
        openInbox,
        openChatConv,
        backToInbox,
        closePanel,
        closeChat,
        fetchConvs,
        sendTyping,
        totalUnread,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}

export const useChat = () => useContext(ChatContext);
